<script>
  import { onMount } from 'svelte';
  import { Chart, registerables } from 'chart.js';
  import { sumBy } from '../lib/utils/array-helpers';
  import { calculatePercentage } from '../lib/utils/calculations';

  let { summary, topN = 14 } = $props();
  let canvas;
  let chart;

  onMount(() => {
    Chart.register(...registerables);

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  });

  // Re-render chart when summary changes or canvas is available
  $effect(() => {
    if (canvas && summary) {
      renderChart();
    }
  });

  function renderChart() {
    // Guard: Check if summary has required data
    if (!summary || !summary.byVendor || !canvas) {
      return;
    }

    // Destroy existing chart before creating new one
    if (chart) {
      chart.destroy();
      chart = null;
    }

    // Also check if Chart.js has an existing chart instance for this canvas
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    // Get vendors sorted by expenses (highest to lowest)
    // Filter out credit card payments
    const vendors = Object.entries(summary.byVendor || {})
      .filter(([vendor, stats]) => {
        const vendorLower = vendor.toLowerCase();
        return stats.amount > 0 &&
               !vendorLower.includes('credit card payment') &&
               !vendorLower.includes('cc payment');
      })
      .sort(([, a], [, b]) => b.amount - a.amount);

    // Limit to top N vendors
    const topVendors = vendors.slice(0, topN);

    // Calculate "Other" category if there are more vendors
    let otherAmount = 0;
    if (vendors.length > topN) {
      otherAmount = sumBy(vendors.slice(topN), ([, stats]) => stats.amount);
    }

    // Clean up vendor names (remove extra whitespace) and truncate
    const labels = topVendors.map(([name]) => {
      // Replace multiple spaces with single space and trim
      const cleaned = name.replace(/\s+/g, ' ').trim();
      return cleaned.length > 20 ? cleaned.substring(0, 20) + '...' : cleaned;
    });
    const data = topVendors.map(([, stats]) => stats.amount);

    // Keep full cleaned names for tooltip
    const fullNames = topVendors.map(([name]) => name.replace(/\s+/g, ' ').trim());

    // Add "Other" if applicable
    if (otherAmount > 0) {
      labels.push('Other');
      data.push(otherAmount);
      fullNames.push('Other');
    }

    console.log('Rendering Vendor Pie Chart with data:', { labels, data });

    // Get colors from CSS variables
    const rootStyles = getComputedStyle(document.documentElement);
    const colors = [
      rootStyles.getPropertyValue('--chart-color-1').trim(),
      rootStyles.getPropertyValue('--chart-color-4').trim(),
      rootStyles.getPropertyValue('--chart-color-2').trim(),
      rootStyles.getPropertyValue('--chart-color-9').trim(),
      rootStyles.getPropertyValue('--chart-color-5').trim(),
      rootStyles.getPropertyValue('--chart-color-6').trim(),
      rootStyles.getPropertyValue('--chart-color-7').trim(),
      rootStyles.getPropertyValue('--chart-color-3').trim(),
      rootStyles.getPropertyValue('--chart-color-8').trim(),
      rootStyles.getPropertyValue('--chart-color-10').trim(),
      rootStyles.getPropertyValue('--color-text-muted').trim(),  // gray (for "Other")
    ];

    const borderColor = rootStyles.getPropertyValue('--color-text-primary').trim();

    chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: colors,
          borderColor: borderColor,
          borderWidth: 0,
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              title: function(context) {
                // Show full vendor name in tooltip title
                return fullNames[context[0].dataIndex];
              },
              label: function(context) {
                const value = context.parsed.x;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = calculatePercentage(value, total).toFixed(1);
                const formatted = new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(value);
                return `${formatted} (${percentage}%)`;
              }
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-text-muted').trim(),
              callback: function(value) {
                return '$' + value.toLocaleString();
              }
            },
            grid: {
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-border').trim()
            }
          },
          y: {
            ticks: {
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-text-muted').trim(),
              font: {
                size: 11
              }
            },
            grid: {
              display: false
            }
          }
        }
      }
    });
  }
</script>

<div class="chart-container">
  <canvas bind:this={canvas}></canvas>
</div>

<style>
  .chart-container {
    position: relative;
    width: 100%;
    height: 350px;
  }
</style>
